import { Rng } from '../../shared';
import { journeyLength, type Journey } from '../sim';
import { waterwaysFor } from './mapGeography';

export interface MapPoint { x: number; y: number }
export interface MapPlace extends MapPoint { id: string; index: number; state: 'past' | 'current' | 'future' }
export interface MapModel {
  seed: string; mode: Journey['mode']; places: MapPlace[]; route: string; travelled: string; waterways: MapPoint[][]; current: MapPlace;
  previous: MapPlace | null; next: MapPlace | null; complete: boolean; preview: boolean;
}

/** The illustration has its own stream and never consumes the expedition RNG. */
export function buildMapModel(journey: Journey, preview = false, previousStep?: number): MapModel {
  const rng = new Rng(journey.seed).stream('map:layout');
  const road = [[73, 310], [179, 174], [298, 296], [413, 150], [534, 270], [636, 119]];
  const crypt = [[74, 302], [76, 137], [263, 126], [262, 292], [448, 301], [448, 133], [633, 130], [633, 294]];
  const count = journeyLength(journey);
  // A closed expedition ends at the last visited place, never at an unexplored room.
  const active = Math.min(count - 1, Math.max(0, journey.step - (journey.status === 'active' ? 0 : 1)));
  const places: MapPlace[] = (journey.mode === 'crypt' ? crypt : road).slice(0, count).map(([x, y], index) => ({
    x: x + rng.int(-7, 7), y: y + rng.int(-7, 7), id: journey.events[index], index,
    state: index < journey.step ? 'past' : index === active ? 'current' : 'future',
  }));
  // A smooth single trail is honest about the existing linear encounter sequence.
  let route = `M${places[0].x} ${places[0].y}`, travelled = '';
  for (let i = 1; i < places.length; i++) {
    const a = places[i - 1], b = places[i], dx = b.x - a.x, dy = b.y - a.y;
    route += journey.mode === 'crypt'
      ? `L${b.x} ${b.y}`
      : `C${a.x + dx * .55} ${a.y + dy * .05} ${b.x - dx * .45} ${b.y - dy * .05} ${b.x} ${b.y}`;
    if (i === active) travelled = route;
  }
  return {
    seed: journey.seed, mode: journey.mode, places, route, travelled, waterways: journey.mode === 'road' ? waterwaysFor(places, journey.seed) : [], current: places[active], next: places[active + 1] ?? null,
    previous: previousStep === undefined || previousStep === active ? null : places[Math.min(count - 1, Math.max(0, previousStep))],
    complete: journey.status === 'complete', preview,
  };
}
